import { useMemo } from "react";
import {
  Product,
  ProductAvailabilityType,
} from "../../../../../graphql/operations";
import StepperGroup from "../../../../Atoms/StepperGroup";
import styles from "./GuestPicker.module.css";

interface OrderItem {
  typeId: string;
  customerType: string;
  quantity: number;
  price: number;
  currency?: string;
  excludeFromPrivateGroupCount: boolean;
}

interface Variant {
  id: string;
  customerType: string;
  translation: {
    name: string;
    description: string;
  };
  minimumPeople: number;
  maximumPeople: number;
  label: string;
  description: string | null;
  amount: number;
  currency: string;
  vegetarian: boolean;
}

type GuestPickerProps = {
  value: Array<OrderItem>;
  presetVariant?: string;
  onVariantChange: (item: OrderItem) => void;
  currencyCode: string;
  email?: string;
} & Pick<Product, "maximumInGroup" | "prices" | "availabilityType">;

const toDatalist = (prices: Product["prices"]) => {
  return prices.map((price) => {
    return {
      id: price.id,
      value: price.minimumPeople || 0,
      label: price.translation.name,
      min: price.minimumPeople || 0,
      max: price.maximumPeople,
      description: price.translation.description,
      vegetarian: price.vegetarian,
      excludeFromPrivateGroupCount: price.excludeFromPrivateGroupCount,
      price: {
        amount: price.amount,
        currency: price.currency,
      },
    };
  });
};

// @todo
// max should depend on the group
// const getMax = prices => prices[0].maximum

export default function GuestPicker({
  value,
  prices,
  presetVariant,
  onVariantChange,
  currencyCode,
  maximumInGroup,
  email,
  availabilityType,
}: GuestPickerProps) {
  // no idea in what case we get variant from query
  const filteredPrices = prices.filter((v) => v.id === presetVariant);
  const pricesToShow = filteredPrices.length > 0 ? filteredPrices : prices;

  const datalist = toDatalist(pricesToShow);
  const priceGroupSizes = value.reduce(
    (acc, orderItem) => ({
      ...acc,
      [orderItem.typeId]: orderItem.quantity,
    }),
    {} as Record<OrderItem["typeId"], OrderItem["quantity"]>
  );

  const numGuests = useMemo(
    () =>
      Object.values(priceGroupSizes).reduce(
        (sum, groupSize) => sum + groupSize,
        0
      ),
    [priceGroupSizes]
  );

  return (
    <div className={styles.BookingFormGuestSelection}>
      <StepperGroup
        currencyCode={currencyCode}
        value={priceGroupSizes}
        max={maximumInGroup}
        datalist={datalist}
        onChange={(id, value) =>
          onVariantChange({
            typeId: id,
            quantity: value,
            price: prices.find((d) => d.id === id).amount,
            currency: prices.find((d) => d.id === id).currency,
            customerType: prices.find((d) => d.id === id)?.translation
              .name as string,
            excludeFromPrivateGroupCount: prices.find((d) => d.id === id)
              .excludeFromPrivateGroupCount,
          })
        }
        isPrivateExclusive={
          availabilityType === ProductAvailabilityType.Private
        }
      />

      {numGuests === maximumInGroup && (
        <a href={`/big-group-tours`} target="_blank" className={styles.Link}>
          More than {maximumInGroup} people in your group? Contact us
        </a>
      )}
    </div>
  );
}
